import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText,
  Stack, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  History as HistoryIcon, Logout, Menu as MenuIcon, SpaceDashboardOutlined, TuneOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../auth';
import { t } from '../../utils/theme';

const drawerWidth = 232;

const menuItems = [
  { text: 'Dashboard', icon: <SpaceDashboardOutlined />, path: '/dashboard' },
  { text: 'History', icon: <HistoryIcon />, path: '/history' },
  { text: 'Settings', icon: <TuneOutlined />, path: '/settings' },
];

/** NSE cash session in IST, computed on the client clock. */
function useMarketClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const mins = ist.getHours() * 60 + ist.getMinutes();
  const weekday = ist.getDay() >= 1 && ist.getDay() <= 5;
  const open = weekday && mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30;
  const time = ist.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return { open, time };
}

export const BrandMark: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <Box sx={{
    width: size, height: size, borderRadius: 2, display: 'grid', placeItems: 'center', flexShrink: 0,
    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: '#fff', fontWeight: 800, fontSize: size * 0.5,
    boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
  }}>Z</Box>
);

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const market = useMarketClock();

  const go = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: t.sidebar, borderRight: `1px solid ${t.border}` }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, height: 64 }}>
        <BrandMark />
        <Box>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>Zerodha Algo</Typography>
          <Typography variant="caption" sx={{ color: t.text3 }}>Private console</Typography>
        </Box>
      </Stack>
      <Divider />
      <List sx={{ px: 1.5, py: 2, flex: 1 }} component="nav" aria-label="Main">
        {menuItems.map((item) => {
          const active = location.pathname.startsWith(item.path);
          return (
            <ListItemButton
              key={item.text} selected={active} onClick={() => go(item.path)}
              aria-current={active ? 'page' : undefined}
              sx={{
                borderRadius: 2, mb: 0.5, py: 1, color: active ? t.text : t.text2,
                '&.Mui-selected': { bgcolor: 'rgba(99,102,241,0.14)', '&:hover': { bgcolor: 'rgba(99,102,241,0.2)' } },
                '&.Mui-selected::before': {
                  content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, bgcolor: t.accent,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: active ? t.accentSoft : t.text3 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: active ? 600 : 500 }} />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ mx: 1.5, mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: t.panel, border: `1px solid ${t.border}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: market.open ? '#22C55E' : t.text3,
                     boxShadow: market.open ? '0 0 0 3px rgba(34,197,94,0.2)' : 'none' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>NSE {market.open ? 'open' : 'closed'}</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ color: t.text2 }}>{market.time} IST</Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: t.text3 }}>Session 09:15 – 15:30, Mon–Fri</Typography>
      </Box>
      <Divider />
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.5 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: t.panel2, color: t.text, fontSize: '0.9rem', border: `1px solid ${t.border}` }}>
          {(user?.username?.[0] || 'U').toUpperCase()}
        </Avatar>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>{user?.username}</Typography>
        <Tooltip title="Log out">
          <IconButton size="small" aria-label="Log out" onClick={async () => { await logout(); navigate('/login'); }}>
            <Logout fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  const paperSx = { boxSizing: 'border-box', width: drawerWidth, border: 'none' };

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {isMobile && (
        <AppBar position="fixed" elevation={0} sx={{ bgcolor: t.sidebar, borderBottom: `1px solid ${t.border}` }}>
          <Toolbar>
            <IconButton edge="start" aria-label="Open menu" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            <BrandMark size={28} />
            <Typography sx={{ fontWeight: 700, ml: 1.25, flex: 1 }}>Zerodha Algo</Typography>
            <Typography variant="body2" sx={{ color: market.open ? '#22C55E' : t.text3, fontWeight: 600 }}>
              {market.open ? 'Market open' : 'Closed'}
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {isMobile ? (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
                  sx={{ '& .MuiDrawer-paper': paperSx }}>
            {drawer}
          </Drawer>
        ) : (
          <Drawer variant="permanent" open sx={{ '& .MuiDrawer-paper': paperSx }}>{drawer}</Drawer>
        )}
      </Box>
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, px: { xs: 2, md: 4 }, py: { xs: 2, md: 3.5 }, maxWidth: 1480, mx: 'auto', width: '100%' }}>
        {isMobile && <Toolbar />}
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
